import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AppContext from "../../../context/appContext";
import useDownloadAttachment from "../../../hooks/useDownloadAttachment";
import LiveRegion from "../../../components/LiveRegion/LiveRegion";
import { announce } from "../../../utils/a11y/announcer";
import useDocumentTitle from "../../../utils/a11y/useDocumentTitle";
import visuallyHiddenSx from "../../../utils/a11y/visuallyHiddenSx";
import { pluralize } from "../../../utils/a11y/ptBr";

import CultureHubToolbar from "./CultureHubToolbar";
import CultureHubGroup from "./CultureHubGroup";
import CultureHubModalSkeleton from "./CultureHubModalSkeleton";

import {
  buildCultureHubGroups,
  getAllAttachmentIds,
} from "./CultureHubModal.utils";

import { getService } from "../../../services/axios/services";

import AssessmentsUploadModal from "../Upload/AssessmentsUploadModal";
import { getAssessmentRelationLabel } from "../Upload/AssessmentsUploadModal.utils";

const TITLE_ID = "culturehub-modal-title";
const PAGE_TITLE_ID = "culturehub-page-title";

const CultureHubModal = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const appContext = useContext(AppContext);
  const { downloadAndZipMultipleAttachments } = useDownloadAttachment();

  const [selectedIds, setSelectedIds] = useState([]);
  const [showOnlyAssessment, setShowOnlyAssessment] = useState(false);
  const [title, setTitle] = useState("");
  const [groups, setGroups] = useState([]);
  const [isAssessmentUploadOpen, setIsAssessmentUploadOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // id whose first fetch has settled; keeps refetches for the same id from showing the skeleton
  const [loadedId, setLoadedId] = useState(null);

  const fetchSourceRef = useRef(null);
  const downloadSourceRef = useRef(null);
  const isModalMountedRef = useRef(true);
  const openerRef = useRef(null);
  const paperRef = useRef(null);
  const titleRef = useRef(null);
  const groupsRef = useRef([]);
  const previousSelectedCountRef = useRef(0);

  groupsRef.current = groups;

  const isOpen = !!id;
  const isLoading = isOpen && loadedId !== id;

  const cancelPendingRequests = useCallback(() => {
    if (fetchSourceRef.current) fetchSourceRef.current.cancel();
    if (downloadSourceRef.current) downloadSourceRef.current.cancel();
  }, []);

  const fetchData = useCallback(
    async (isMounted = () => true) => {
      if (!id) return;

      if (fetchSourceRef.current) fetchSourceRef.current.cancel();

      const source = axios.CancelToken.source();
      fetchSourceRef.current = source;
      const options = { cancelToken: source.token };

      try {
        const [project, attachments, assessments] = await Promise.all([
          getService({
            endpoint: `dramaturgies/${id}`,
            options,
          }),
          getService({
            endpoint: `CultureHub/projects/${id}/attachments`,
            api: "v2",
            options,
          }),
          getService({
            endpoint: `Assessments/contents/${id}`,
            api: "v2",
            options,
          }),
        ]);

        if (!isMounted()) return;

        setTitle(project?.data?.title ?? "");
        setGroups(
          buildCultureHubGroups(
            attachments ?? {},
            assessments?.data ?? assessments ?? []
          )
        );
        setLoadedId(id);
      } catch (err) {
        if (axios.isCancel(err) || !isMounted()) return;

        console.error("Erro ao buscar dados do CultureHub:", err);

        announce("Não foi possível carregar os dados do projeto.", "assertive");

        setTitle("");
        setGroups([]);
        setLoadedId(id);
      }
    },
    [id]
  );

  useEffect(() => {
    let mounted = true;

    if (id) {
      setSelectedIds([]);
    } else {
      // reopening the same id must go through the loading flow again, not render stale data at once
      setLoadedId(null);
    }

    fetchData(() => mounted);

    return () => {
      mounted = false;
      cancelPendingRequests();
    };
  }, [id, fetchData, cancelPendingRequests]);

  useEffect(() => {
    isModalMountedRef.current = true;

    return () => {
      isModalMountedRef.current = false;
    };
  }, []);

  const visibleGroups = useMemo(() => {
    if (!showOnlyAssessment) {
      return groups;
    }

    return groups
      .map((group) => ({
        ...group,
        sections: group.sections.filter(
          (section) => section.kind === "Assessment"
        ),
      }))
      .filter((group) => group.sections.length > 0);
  }, [groups, showOnlyAssessment]);

  const allAttachmentIds = useMemo(
    () => getAllAttachmentIds(visibleGroups),
    [visibleGroups]
  );

  const selectedAttachments = useMemo(
    () =>
      visibleGroups.flatMap((group) =>
        group.sections.flatMap((section) =>
          section.attachments.filter((attachment) =>
            selectedIds.includes(attachment.id)
          )
        )
      ),
    [visibleGroups, selectedIds]
  );

  const assessmentRelationLabel = useMemo(
    () => getAssessmentRelationLabel(selectedIds, groups),
    [selectedIds, groups]
  );

  const isAllSelected =
    allAttachmentIds.length > 0 &&
    allAttachmentIds.every((attachmentId) =>
      selectedIds.includes(attachmentId)
    );

  useDocumentTitle(
    isOpen && !isLoading && title ? `${title} | HUB de Cultura | Script` : ""
  );

  // MUI's scroll lock pads body for the scrollbar it measured at mount; on a deep link that leaves an empty strip on the right
  useEffect(() => {
    if (!isOpen) return undefined;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    return () => {
      root.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // once data replaces the skeleton, sends focus to the title unless the user already moved inside the dialog
  useEffect(() => {
    if (!isOpen || isLoading) return;

    const active = document.activeElement;
    const paper = paperRef.current;

    if (!paper || active === paper || !paper.contains(active)) {
      if (titleRef.current) titleRef.current.focus();
    }

    const total = getAllAttachmentIds(groupsRef.current).length;
    announce(
      `${total} ${pluralize(total, "arquivo disponível", "arquivos disponíveis")}.`
    );
  }, [isOpen, isLoading]);

  useEffect(() => {
    const count = selectedIds.length;

    if (count === previousSelectedCountRef.current) return;
    previousSelectedCountRef.current = count;

    announce(
      count === 0
        ? "Nenhum item selecionado."
        : `${count} ${pluralize(count, "item selecionado", "itens selecionados")}.`
    );
  }, [selectedIds]);

  // captured before the dialog moves focus; MUI would restore it while #root is still aria-hidden
  useLayoutEffect(() => {
    if (!isOpen) return;

    const active = document.activeElement;
    openerRef.current = active && active !== document.body ? active : null;
  }, [isOpen]);

  // with no opener to restore focus to (deep link), falls back to the page title instead of <body>
  const handleExited = () => {
    const opener = openerRef.current;
    openerRef.current = null;

    if (opener && opener.isConnected) {
      opener.focus();
      return;
    }

    const active = document.activeElement;

    if (!active || active === document.body) {
      const pageTitle = document.getElementById(PAGE_TITLE_ID);
      if (pageTitle) pageTitle.focus();
    }
  };

  const handleClose = () => {
    cancelPendingRequests();

    setSelectedIds([]);
    setShowOnlyAssessment(false);

    navigate({ pathname: "/hub-cultura", search: location.search });
  };

  const handleToggleAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
      return;
    }

    setSelectedIds(allAttachmentIds);
  };

  const handleToggleAttachment = (attachmentId) => {
    setSelectedIds((current) =>
      current.includes(attachmentId)
        ? current.filter((item) => item !== attachmentId)
        : [...current, attachmentId]
    );
  };

  const handleToggleGroup = (attachmentIds) => {
    const isGroupSelected = attachmentIds.every((attachmentId) =>
      selectedIds.includes(attachmentId)
    );

    setSelectedIds((current) => {
      if (isGroupSelected) {
        return current.filter(
          (attachmentId) => !attachmentIds.includes(attachmentId)
        );
      }

      return [...new Set([...current, ...attachmentIds])];
    });
  };

  const handleDownload = async () => {
    if (isDownloading) return;

    const source = axios.CancelToken.source();
    downloadSourceRef.current = source;

    setIsDownloading(true);
    announce("Preparando o download dos arquivos selecionados…");

    try {
      await downloadAndZipMultipleAttachments({
        contentId: id,
        filesArray: selectedAttachments,
        origin: "hub-cultura",
        mode: 0,
        title,
        setLoading: appContext.setShowGlassLoading,
        cancelToken: source.token,
      });
    } finally {
      if (downloadSourceRef.current === source) {
        downloadSourceRef.current = null;
      }
      if (isModalMountedRef.current) setIsDownloading(false);
    }
  };

  const handleDeleteAssessment = useCallback(
    async (deletedAttachment) => {
      if (!deletedAttachment) return;

      setSelectedIds((current) =>
        current.filter((attachmentId) => attachmentId !== deletedAttachment.id)
      );

      await fetchData();
      if (titleRef.current) titleRef.current.focus();
    },
    [fetchData]
  );

  const handleToggleAssessment = () => {
    announce(
      showOnlyAssessment
        ? "Exibindo todos os itens."
        : "Exibindo apenas pareceres."
    );
    setShowOnlyAssessment((current) => !current);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      disableScrollLock
      disableRestoreFocus
      fullWidth
      maxWidth="lg"
      aria-labelledby={TITLE_ID}
      TransitionProps={{ onExited: handleExited }}
      PaperProps={{
        ref: paperRef,
        "aria-modal": true,
        sx: {
          borderRadius: "8px",
          // a plain 600px min-height overrides the paper's max-height on short screens and crops it
          minHeight: "min(600px, calc(100% - 64px))",
          // only DialogContent scrolls; the paper's default overflow-y: auto adds a second scrollbar
          overflow: "hidden",
        },
      }}
    >
      <LiveRegion />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          px: 3,
          pt: 1,
          pb: 1,
        }}
      >
        <Typography
          component="h2"
          id={TITLE_ID}
          ref={titleRef}
          tabIndex={-1}
          sx={{
            fontSize: "22px",
            fontWeight: 600,
            color: "#3D3D3D",
            ...(isLoading && { width: "40%" }),
          }}
        >
          {isLoading ? (
            <>
              <Skeleton
                variant="rounded"
                animation="wave"
                aria-hidden="true"
                width="100%"
                height={32}
              />
              <Box component="span" sx={visuallyHiddenSx}>
                Carregando informações…
              </Box>
            </>
          ) : (
            <>
              {title}
              {!title && (
                <Box component="span" sx={visuallyHiddenSx}>
                  Detalhes do projeto
                </Box>
              )}
            </>
          )}
        </Typography>

        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Fechar detalhes do projeto"
          sx={{ width: 48, height: 48, flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          px: 3,
          py: 3,
        }}
      >
        {isLoading ? (
          <CultureHubModalSkeleton />
        ) : (
          <>
            <CultureHubToolbar
              selectedCount={selectedIds.length}
              isAllSelected={isAllSelected}
              onToggleAll={handleToggleAll}
              showOnlyAssessment={showOnlyAssessment}
              onToggleAssessment={handleToggleAssessment}
              onUploadAssessment={() => setIsAssessmentUploadOpen(true)}
              onDownload={handleDownload}
              isDownloading={isDownloading}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {visibleGroups.map((group) => (
                <CultureHubGroup
                  key={group.id}
                  group={group}
                  selectedIds={selectedIds}
                  onToggleAttachment={handleToggleAttachment}
                  onToggleGroup={handleToggleGroup}
                  onDeleteAssessment={handleDeleteAssessment}
                />
              ))}
            </Box>
          </>
        )}
      </DialogContent>

      {!isLoading && (
        <AssessmentsUploadModal
          open={isAssessmentUploadOpen}
          onClose={() => setIsAssessmentUploadOpen(false)}
          onSuccess={fetchData}
          relationLabel={assessmentRelationLabel}
          attachmentIds={selectedIds}
        />
      )}
    </Dialog>
  );
};

export default CultureHubModal;
